import { useMemo } from 'react';
import { Table, useOnScrollDown } from 'redesign';
import { InfiniteQueryObserverSuccessResult } from 'react-query';
import { useSiteDetection } from 'shared/hooks';
import { RenderUnsettledUI } from 'shared/components';
import { PaymentInfiniteResponse } from 'api/services/finance/payments.schema';
import { DateTime } from 'luxon';
import { getPaymentColumnDefinition } from './payment-column-definition';
import {
  getPaymentsData,
  PaymentsColumnDefinition,
  PaymentsParsedData,
  paymentStates,
} from './payments.types';
import { useGetPayments } from './use-get-payments';

type TableProps = {
  infiniteQueryResult: InfiniteQueryObserverSuccessResult<PaymentInfiniteResponse, unknown>;
};

export const PaymentsTableDataProvider = () => {
  const { site } = useSiteDetection();
  const page = 0;
  const infiniteQueryResult = useGetPayments(page, site);

  if (infiniteQueryResult.status !== 'success') {
    return <RenderUnsettledUI data={infiniteQueryResult} />;
  }
  return <PaymentsTable infiniteQueryResult={infiniteQueryResult} />;
};

export const PaymentsTable = (props: TableProps) => {
  const { infiniteQueryResult } = props;
  const flatData = useMemo(
    () => infiniteQueryResult.data.pages.flatMap((page) => page.items),
    [infiniteQueryResult.data]
  );
  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: infiniteQueryResult,
    flatData: flatData,
  });

  const columns = useMemo(() => getPaymentColumnDefinition(), []);
  const tableData = useMemo(() => getParsedTableData(flatData), [infiniteQueryResult.data]);

  return (
    <Table<PaymentsColumnDefinition>
      stickyHeader={true}
      columns={columns}
      data={tableData}
      tableStyle={{ tableLayout: 'fixed' }}
      onScroll={fetchIfBottomScrolled}
    ></Table>
  );
};

const getParsedTableData = (items: getPaymentsData[]): PaymentsParsedData[] => {
  const parsedData = items.map((item) => {
    const generatedInvoiceObj =
      !!item.invoiceNumber && !!item.invoiceUrl
        ? { invoice: { invoiceUrl: item.invoiceUrl, invoiceReference: item.invoiceNumber } }
        : {};
    return {
      workerName: item.workerName,
      paymentId: item.paymentId,
      creationDate: DateTime.fromISO(item.creationDate),
      status: getTranslatedPaymentStatus(item.status as paymentStates),
      dueDate: DateTime.fromISO(item.dueDate),
      ...generatedInvoiceObj,
    };
  });
  return parsedData;
};

const getTranslatedPaymentStatus = (status: paymentStates) => {
  switch (status) {
    case paymentStates.CREATED:
      return 'Aangemaakt';
    case paymentStates.NOT_CREATED:
      return 'Niet aangemaakt';
    case paymentStates.PAID_AUTOMATICALLY:
      return 'Automatisch uitbetaald';
    case paymentStates.PAID_EARLY:
      return 'Vroeg uitbetaald';
    case paymentStates.READY_FOR_AUTOMATIC_PAYOUT:
      return 'Klaar voor automatische uitbetaling';
    case paymentStates.READY_FOR_EARLY_PAYOUT:
      return 'Klaar voor vroege uitbetaling';
  }
};
